/**
 * Development utility functions for testing feedback modal functionality
 * These should only be used during development and testing
 *
 * Note: Modal only shows on homepage ("/"), so navigate to root to see it
 */

// Clear all feedback modal data from localStorage
export const clearFeedbackData = () => {
  localStorage.removeItem("feedbackModalData")
  console.log("Feedback modal data cleared")
}

// Get current feedback modal data for debugging
export const getFeedbackDebugData = () => {
  try {
    const stored = localStorage.getItem("feedbackModalData")
    const data = stored ? JSON.parse(stored) : null
    console.log("Current feedback data:", data)
    return data
  } catch (error) {
    console.error("Error reading feedback data:", error)
    return null
  }
}

// Simulate multiple episode completions for testing
export const simulateEpisodePlays = (count: number, isLoggedUser: boolean = true) => {
  try {
    const stored = localStorage.getItem("feedbackModalData")
    const data = stored
      ? JSON.parse(stored)
      : {
          episodesPlayed: [],
          anonymousEpisodesCount: 0,
          usageTimeMinutes: 0,
        }

    if (isLoggedUser) {
      // Add unique episode IDs
      for (let i = 0; i < count; i++) {
        const episodeId = `test-episode-${Date.now()}-${i}`
        if (!data.episodesPlayed.includes(episodeId)) {
          data.episodesPlayed.push(episodeId)
        }
      }
    } else {
      // Increment anonymous count
      data.anonymousEpisodesCount += count
    }

    localStorage.setItem("feedbackModalData", JSON.stringify(data))
    console.log(`Simulated ${count} episode completions for ${isLoggedUser ? "logged" : "anonymous"} user`)
    return data
  } catch (error) {
    console.error("Error simulating episode completions:", error)
    return null
  }
}

// Simulate usage time for anonymous users
export const simulateUsageTime = (minutes: number) => {
  try {
    const stored = localStorage.getItem("feedbackModalData")
    const data = stored
      ? JSON.parse(stored)
      : {
          episodesPlayed: [],
          anonymousEpisodesCount: 0,
          usageTimeMinutes: 0,
        }

    data.usageTimeMinutes += minutes
    localStorage.setItem("feedbackModalData", JSON.stringify(data))
    console.log(`Simulated ${minutes} minutes of usage time`)
    return data
  } catch (error) {
    console.error("Error simulating usage time:", error)
    return null
  }
}

// Set feedback delay (for testing delay functionality)
export const setFeedbackDelay = (daysFromNow: number) => {
  try {
    const stored = localStorage.getItem("feedbackModalData")
    const data = stored
      ? JSON.parse(stored)
      : {
          episodesPlayed: [],
          anonymousEpisodesCount: 0,
          usageTimeMinutes: 0,
        }

    const delayDate = new Date(Date.now() + daysFromNow * 24 * 60 * 60 * 1000)
    data.delayUntil = delayDate.toISOString()
    localStorage.setItem("feedbackModalData", JSON.stringify(data))
    console.log(`Set feedback delay until ${delayDate.toLocaleDateString()}`)
    return data
  } catch (error) {
    console.error("Error setting feedback delay:", error)
    return null
  }
}

// Debug function to check all feedback modal conditions
export const debugFeedbackConditions = () => {
  try {
    const stored = localStorage.getItem("feedbackModalData")
    const data = stored
      ? JSON.parse(stored)
      : {
          episodesPlayed: [],
          anonymousEpisodesCount: 0,
          usageTimeMinutes: 0,
        }

    const now = new Date()
    let timeConditionMet = true
    let timeConditionReason = "No previous feedback"

    // Check delay conditions
    if (data.delayUntil) {
      const delayUntil = new Date(data.delayUntil)
      if (now < delayUntil) {
        timeConditionMet = false
        timeConditionReason = `Delayed until ${delayUntil.toLocaleDateString()} (${Math.ceil(
          (delayUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
        )} days remaining)`
      }
    }

    // Check normal delay period
    if (timeConditionMet && data.lastFeedbackDate) {
      const lastFeedback = new Date(data.lastFeedbackDate)
      const daysSinceLastFeedback = (now.getTime() - lastFeedback.getTime()) / (1000 * 60 * 60 * 24)

      if (daysSinceLastFeedback < 30) {
        timeConditionMet = false
        timeConditionReason = `Only ${Math.floor(daysSinceLastFeedback)} days since last feedback (need 30)`
      } else {
        timeConditionReason = `${Math.floor(daysSinceLastFeedback)} days since last feedback (✅)`
      }
    }

    const episodesConditionMet = data.episodesPlayed.length >= 3
    const currentPath = window.location.pathname

    const result = {
      rawData: data,
      conditions: {
        episodes: {
          met: episodesConditionMet,
          current: data.episodesPlayed.length,
          required: 3,
          status: episodesConditionMet ? "✅ MET" : "❌ NOT MET",
        },
        time: {
          met: timeConditionMet,
          reason: timeConditionReason,
          status: timeConditionMet ? "✅ MET" : "❌ NOT MET",
        },
        location: {
          met: currentPath === "/",
          current: currentPath,
          required: "/",
          status: currentPath === "/" ? "✅ MET" : "❌ NOT MET",
        },
      },
      shouldShowModal: episodesConditionMet && timeConditionMet && currentPath === "/",
    }

    console.log("🔍 Feedback Modal Debug Report:", result)
    return result
  } catch (error) {
    console.error("Error debugging feedback conditions:", error)
    return null
  }
}

// Test functions for development console
if (typeof window !== "undefined") {
  // @ts-ignore - Adding to window for development testing
  window.feedbackModalUtils = {
    clear: clearFeedbackData,
    debug: getFeedbackDebugData,
    debugConditions: debugFeedbackConditions,
    simulateEpisodes: simulateEpisodePlays,
    simulateUsage: simulateUsageTime,
    setDelay: setFeedbackDelay,
  }
}
